import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { ChevronDownIcon, SearchIcon, MessageCircleIcon } from 'lucide-react';
export function FAQPage() {
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const categories = [{
    id: 'all',
    label: 'All Questions'
  }, {
    id: 'orders',
    label: 'Orders & Delivery'
  }, {
    id: 'prescription',
    label: 'Prescriptions'
  }, {
    id: 'payment',
    label: 'Payment & Pricing'
  }, {
    id: 'account',
    label: 'Account & Profile'
  }, {
    id: 'returns',
    label: 'Returns & Refunds'
  }];
  const faqs = [{
    category: 'orders',
    question: 'How long does delivery take?',
    answer: 'Standard delivery typically takes 1-3 business days within major cities. Express delivery is available for same-day or next-day delivery in select areas. Delivery times may vary based on your location and product availability.'
  }, {
    category: 'orders',
    question: 'Can I track my order?',
    answer: 'Yes! Once your order is dispatched, you will receive a tracking number via SMS and email. You can track your order in real-time through the "My Orders" section in your account or by using the tracking link provided.'
  }, {
    category: 'orders',
    question: 'What are the delivery charges?',
    answer: 'Delivery is FREE for orders above SAR 200. For orders below SAR 200, a delivery fee of SAR 15 applies. Express delivery options may have additional charges.'
  }, {
    category: 'prescription',
    question: 'How do I upload my prescription?',
    answer: 'You can upload your prescription through our website or mobile app. Simply take a clear photo of your prescription and upload it during checkout or through the "Upload Prescription" section. Our pharmacists will verify it before processing your order.'
  }, {
    category: 'prescription',
    question: 'Is my prescription information secure?',
    answer: 'Absolutely. We take your privacy seriously. All prescription information is encrypted and stored securely in compliance with healthcare data protection regulations. Only authorized pharmacists have access to verify your prescriptions.'
  }, {
    category: 'prescription',
    question: 'Can I order without a prescription?',
    answer: 'Over-the-counter (OTC) medications, supplements, and health products can be ordered without a prescription. However, prescription medications require a valid prescription from a licensed healthcare provider.'
  }, {
    category: 'payment',
    question: 'What payment methods do you accept?',
    answer: 'We accept various payment methods including Visa, Mastercard, Mada, Apple Pay, STC Pay, Tabby (Buy Now Pay Later), Tamara (Buy Now Pay Later), and Cash on Delivery (COD) for eligible orders.'
  }, {
    category: 'payment',
    question: 'Is it safe to pay online?',
    answer: 'Yes, all online transactions are processed through secure, encrypted payment gateways. We use industry-standard SSL encryption to protect your financial information. We never store your complete card details.'
  }, {
    category: 'payment',
    question: 'Can I get an invoice for my order?',
    answer: 'Yes, a digital invoice is automatically generated for every order and sent to your registered email address. You can also download invoices from the "My Orders" section in your account.'
  }, {
    category: 'account',
    question: 'How do I create an account?',
    answer: 'Click on "Sign In" at the top of the page, then select "Register". You can sign up using your email address or phone number. Creating an account allows you to track orders, save addresses, and earn loyalty points.'
  }, {
    category: 'account',
    question: 'What are loyalty points and how do I earn them?',
    answer: 'Loyalty points are rewards you earn with every purchase. You earn 1 point for every SAR 1 spent. Points can be redeemed for discounts on future orders. Additional points can be earned through referrals and special promotions.'
  }, {
    category: 'account',
    question: 'How do I reset my password?',
    answer: 'Click on "Sign In", then select "Forgot Password". Enter your registered email or phone number, and we will send you a verification code to reset your password.'
  }, {
    category: 'returns',
    question: 'What is your return policy?',
    answer: 'We accept returns within 14 days of delivery for unopened products in their original packaging. Prescription medications and personal care items cannot be returned for safety and hygiene reasons. Please refer to our Return Policy page for complete details.'
  }, {
    category: 'returns',
    question: 'How do I return a product?',
    answer: 'To initiate a return, go to "My Orders" in your account, select the order, and click "Request Return". Our team will review your request and provide return instructions. Approved returns can be picked up from your location or dropped off at our stores.'
  }, {
    category: 'returns',
    question: 'When will I receive my refund?',
    answer: 'Once we receive and inspect your returned item, refunds are processed within 5-7 business days. The refund will be credited to your original payment method. For Cash on Delivery orders, refunds are issued as store credit or bank transfer.'
  }];
  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <HelmetProvider data-id="element-7010" data-mp="24wtipwvab18b">
      <SEO title="Frequently Asked Questions (FAQ)" description="Find answers to common questions about ordering, delivery, prescriptions, payments, and returns at Innova Pharmacy. Get help 24/7." canonical="/faq" data-id="element-7011" data-mp="1zw5rpb722ke3" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-7012" data-mp="29ulb0rzucpd1">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-7013" data-mp="1m5ae5llm3f46">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} disableShrink={true} showAnnouncement={true} data-id="element-7014" data-mp="1h5ed9v87o3su" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-7015" data-mp="12g00se9c9698">
          {/* Mobile Header Overlay */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-7016" data-mp="yeo9m4atgcyx">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-7017" data-mp="1naqvcdhp51xk" />
          </div>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'FAQs'
        }]} title="Frequently Asked Questions" description="Find answers to the most common questions about our services, orders, and policies." data-id="element-7018" data-mp="219gv60vwhfpm" />
          <div className="container mx-auto px-4 py-8" data-id="element-7019" data-mp="2jzyj7s5sfn4n">
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8" data-id="element-7020" data-mp="2k1sueer9c0vj">
              <div className="relative" data-id="element-7021" data-mp="2p0432m5tg5c6">
                <input type="text" placeholder="Search for answers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent shadow-sm" data-id="element-7022" data-mp="2owyhhgwebll7" />
                <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-7023" data-mp="2u5q0wft5k1wx" />
              </div>
            </div>
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 justify-center mb-8" data-id="element-7024" data-mp="3muok7g90ha3t">
              {categories.map(category => <button key={category.id} onClick={() => setActiveCategory(category.id)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === category.id ? 'bg-[#1E2B55] text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-7025" data-mp="3d2r723yg082g">
                  {category.label}
                </button>)}
            </div>
            {/* FAQ List */}
            <div className="max-w-4xl mx-auto" data-id="element-7026" data-mp="105q8y8bqk3hj">
              <div className="space-y-4" data-id="element-7027" data-mp="v5u5qbav4g31">
                {filteredFaqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-7028" data-mp="195mqzr3me43m" data-mpi={index}>
                    <button onClick={() => toggleFAQ(index)} className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors" data-id="element-7029" data-mp="1j2hm000opmd5">
                      <span className="font-semibold text-gray-900 pr-4" data-id="element-7030" data-mp="1rtrs0qsw7ayq">
                        {faq.question}
                      </span>
                      <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${openIndex === index ? 'transform rotate-180' : ''}`} data-id="element-7031" data-mp="1nvu2tnz4govc" />
                    </button>
                    {openIndex === index && <div className="px-6 pb-4" data-id="element-7032" data-mp="2gdv1qaih5goz">
                        <p className="text-gray-600 leading-relaxed" data-id="element-7033" data-mp="2lfams2knjn7d">
                          {faq.answer}
                        </p>
                      </div>}
                  </div>)}
              </div>
              {filteredFaqs.length === 0 && <div className="text-center py-12" data-id="element-7034" data-mp="1xp6l40r0xxj0">
                  <SearchIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-7035" data-mp="1xndeumkbpfbj" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2" data-id="element-7036" data-mp="22hq277ox7ask">
                    No results found
                  </h3>
                  <p className="text-gray-500" data-id="element-7037" data-mp="1sqd9kdvasglh">
                    Try adjusting your search or category filter
                  </p>
                </div>}
            </div>
            {/* Contact CTA */}
            <div className="max-w-4xl mx-auto mt-12 bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-8 text-white text-center" data-id="element-7038" data-mp="1ispajbqnsmxi">
              <MessageCircleIcon className="w-12 h-12 mx-auto mb-4" data-id="element-7039" data-mp="3lgfi5qfcml2h" />
              <h3 className="text-2xl font-bold mb-2" data-id="element-7040" data-mp="3gnup4r49oc00">Still have questions?</h3>
              <p className="text-blue-100 mb-6" data-id="element-7041" data-mp="3borum0lwxo6a">
                Our customer support team is here to help you 24/7
              </p>
              <a href="/contact-us" className="inline-block bg-white text-[#1E2B55] font-bold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-7042" data-mp="36r9kw802n4x8">
                Contact Us
              </a>
            </div>
          </div>
        </main>
        <Footer data-id="element-7043" data-mp="3uehs4vh28o1w" />
        <MobileFooterNav data-id="element-7044" data-mp="3udy9fcjl5vmt" />
        <PharmacistChat data-id="element-7045" data-mp="3uhdoifd9f4o8" />
        <div className="h-16 md:hidden" data-id="element-7046" data-mp="3qe7in8u27fia"></div>
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-7047" data-mp="3qde9603pubwg" />
      </div>
    </HelmetProvider>;
}